// client/src/utils/parseTabular.ts
// Shared CSV / TSV / XLSX parsing for the data-import surfaces.
//
// Every cell stays a string — no date or number coercion. This matters for
// ID-shaped values like "03784-003" that XLSX otherwise reads as a YYYY-MM date
// and converts to an Excel serial number, and for ZIPs like "06520" that would
// otherwise lose their leading zero.

import * as XLSX from 'xlsx';

export interface TabularData {
  headers: string[];
  rows: Record<string, string>[];
}

/** Split a single CSV line, honoring double-quoted fields with "" escapes. */
export function splitCsvLine(line: string): string[] {
  const out: string[] = [];
  let cur = '';
  let inQuote = false;
  for (let i = 0; i < line.length; i++) {
    const c = line[i];
    if (inQuote) {
      if (c === '"') {
        if (line[i + 1] === '"') { cur += '"'; i++; }
        else inQuote = false;
      } else {
        cur += c;
      }
    } else if (c === ',') {
      out.push(cur); cur = '';
    } else if (c === '"' && cur === '') {
      inQuote = true;
    } else {
      cur += c;
    }
  }
  out.push(cur);
  return out;
}

/** Build rows from a header array and an array of column arrays. */
function toRows(headers: string[], lines: string[][]): Record<string, string>[] {
  return lines.map((cols) => {
    const obj: Record<string, string> = {};
    headers.forEach((h, i) => { obj[h] = (cols[i] ?? '').trim(); });
    return obj;
  });
}

/** Parse CSV-as-text. */
export function parseCsvText(text: string): TabularData {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);   // strip BOM
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = splitCsvLine(lines[0]).map((h) => h.trim());
  return { headers, rows: toRows(headers, lines.slice(1).map(splitCsvLine)) };
}

/** Parse tab-separated text. */
export function parseTsvText(text: string): TabularData {
  if (text.charCodeAt(0) === 0xfeff) text = text.slice(1);
  const lines = text.split(/\r?\n/).filter((l) => l.length > 0);
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = (lines[0] || '').split('\t').map((h) => h.trim());
  return { headers, rows: toRows(headers, lines.slice(1).map((l) => l.split('\t'))) };
}

/**
 * Parse an uploaded .csv / .txt / .tsv / .xlsx / .xls file into string cells.
 */
export function parseTabularFile(file: File): Promise<TabularData> {
  const isPlainText = /\.(csv|txt|tsv)$/i.test(file.name);
  const isTsv = /\.tsv$/i.test(file.name);

  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        if (isPlainText) {
          const text = new TextDecoder('utf-8').decode(e.target?.result as ArrayBuffer);
          resolve(isTsv ? parseTsvText(text) : parseCsvText(text));
          return;
        }

        // .xlsx / .xls — disable date coercion and force string output.
        const data = new Uint8Array(e.target?.result as ArrayBuffer);
        const wb = XLSX.read(data, { type: 'array', cellDates: false });
        const ws = wb.Sheets[wb.SheetNames[0]];
        const aoa = XLSX.utils.sheet_to_json<unknown[]>(ws, { defval: '', raw: false, header: 1 });
        if (aoa.length === 0) { resolve({ headers: [], rows: [] }); return; }
        const headers = (aoa[0] as unknown[]).map((h) => String(h ?? '').trim());
        const rows = aoa.slice(1).map((r) => {
          const obj: Record<string, string> = {};
          headers.forEach((h, i) => { obj[h] = String((r as unknown[])[i] ?? '').trim(); });
          return obj;
        });
        resolve({ headers, rows });
      } catch (err: any) {
        reject(new Error(`Failed to parse file: ${err.message}`));
      }
    };
    reader.onerror = () => reject(new Error('Failed to read file'));
    reader.readAsArrayBuffer(file);
  });
}

/**
 * Download rows as a UTF-8 CSV with a BOM (so Excel detects the encoding).
 */
export function downloadCsv(filename: string, headers: string[], rows: (string | number | null | undefined)[][]) {
  const esc = (v: string | number | null | undefined): string => {
    if (v == null || v === '') return '';
    const s = String(v);
    return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  };
  const lines = [headers.map(esc).join(','), ...rows.map((r) => r.map(esc).join(','))];
  const csv = '﻿' + lines.join('\r\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
